'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { storeFront } from '@/lib/utils/routeLinks';
import useAuth from '@/hooks/useAuth';
import ImageWithFallback from '../shared/ImageWithFallback';
import { useOnlineShopTotalCount } from '@/stores/useOnlineShopTotalCount';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import { useTailwindBreakpoint } from '@/hooks/useTailwindBreakpoint';
import { MouseEvent } from 'react';
import { pushEvent } from '@/lib/utils/functions';
// import {
//   getClickIdForEvent,
//   markEventAsTracked,
// } from '@/lib/utils/adcashTracking';

type Props = {
  dictionary: any;
  header?: any;
  heroImage?: any;
  videoLink?: any;
  lang: 'en' | 'de';
  data?: any;
};

export default function WelcomeSection({
  dictionary,
  heroImage,
  lang,
  data,
}: Props) {
  const router = useRouter();
  const t = useTranslations();
  const sectionData = data?.value;
  const { totalOnlineShop } = useOnlineShopTotalCount();
  const { isAnyUserLoggedIn } = useAuth();

  const { lte } = useTailwindBreakpoint();
  const isMobileAndTab = lte('lg');
  // get current month;
  const currentMonth = new Date().getMonth() + 1;
  // if current month is december show december default image, else show normal default image
  let contestBannerLink = 'https://blog.lentho.com/gewinnspiel';
  let defaultDesktopHeroImage =
    lang == 'de'
      ? '/hero-section/Gewinnspielbanner_Desktop_1887x656.png'
      : '/hero-section/Gewinnspielbanner_Desktop_1887x656_e.png';
  let defaultMobileHeroImage =
    lang == 'de'
      ? '/hero-section/Gewinnspielbanner_Mobile_1200x456.png'
      : '/hero-section/Gewinnspielbanner_Mobile_1200x456_e.png';

  if (currentMonth === 12) {
    contestBannerLink = 'https://blog.lentho.com/adventskalender-gewinnspiel/';
    defaultDesktopHeroImage =
      lang == 'de'
        ? '/hero-section/Adventskalender_Desktop_1887x656_d.png'
        : '/hero-section/Adventskalender_Desktop_1887x656_e.png';
    defaultMobileHeroImage =
      lang == 'de'
        ? '/hero-section/Advengtskalender_Mobile_1200x456_d.png'
        : '/hero-section/Advengtskalender_Mobile_1200x456_e.png';
  }

  // Reusable Adcash tracking function - only track if not already tracked
  // const trackAdcashButtonClick = async (
  //   buttonContext: string
  // ): Promise<void> => {
  //   const adcashClickId = getClickIdForEvent('button_click');
  //   if (!adcashClickId) return;

  //   const payload = JSON.stringify({
  //     clickId: adcashClickId,
  //     buttonContext,
  //   });

  //   try {
  //     // Use fetch and wait for response to ensure tracking completes before navigation
  //     const response = await fetch('/api/adcash/button-click', {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: payload,
  //       keepalive: true,
  //     });

  //     if (response.ok) {
  //       markEventAsTracked('button_click');
  //     }
  //   } catch (err) {
  //     console.error('[Adcash] Button tracking failed:', err);
  //   }
  // };

  const handleOnlineShopIconLink = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // Track click immediately without waiting for response
    // trackAdcashButtonClick('hero_online_shop_icon');

    // Navigate using Next.js router
    if (
      sectionData?.online_shop?.right_icon_link &&
      sectionData?.online_shop?.right_icon_link.startsWith('/')
    ) {
      router.push(sectionData?.online_shop?.right_icon_link);
    } else if (
      sectionData?.online_shop?.right_icon_link &&
      !sectionData?.online_shop?.right_icon_link.startsWith('/')
    ) {
      window.open(sectionData.online_shop.right_icon_link);
    } else {
      router.push(`${storeFront.onlineShops.de()}?tag_ids=228`);
    }
  };

  // const handleContestBannerClick = async () => {
  //   await trackAdcashButtonClick('hero_contest_banner');
  // };

  let onlineShopLink = storeFront.onlineShops.de();
  let onlineShopLinkTarget = '_self';
  if (sectionData?.online_shop.slug) {
    onlineShopLink = sectionData?.online_shop.slug;
    if (!onlineShopLink?.startsWith('/')) {
      onlineShopLinkTarget = '_blank';
    }
  }

  return (
    <>
      <section className="order-area relative bg-primary-50">
        <div className="container mx-auto space-y-4 px-4 pb-16 pt-10 md:py-[75px] lg:space-y-[70px] lg:pt-20">
          {/* Top Row */}
          <div className="grid gap-y-8 lg:grid-cols-2 lg:gap-x-8 lg:gap-y-0">
            {/* Left column */}
            <div
              className={cn(
                'mb-8 flex w-full flex-col space-y-3 lg:mb-0 lg:space-y-0 2xl:max-w-[80%]',
                isAnyUserLoggedIn ? 'justify-center' : ''
              )}
            >
              <div className="flex flex-col items-center space-y-3 lg:items-start lg:space-y-5">
                <h1
                  className="px-3 text-center text-[45px] font-black leading-[100%] text-[#C32F76] sm:text-h1 md:leading-[110%] lg:px-0 lg:text-left lg:text-[5rem]"
                  dangerouslySetInnerHTML={{
                    __html:
                      sectionData?.h1?.[lang] ||
                      dictionary.storefront.home.hero_section.header,
                  }}
                />
                <p
                  className="mb-[15px] block max-w-[250px] text-center text-[26px] font-medium leading-[30px] text-title sm:max-w-[310px] lg:max-w-[410px] lg:text-left lg:text-[30px]"
                  dangerouslySetInnerHTML={{
                    __html:
                      sectionData?.desc?.[lang] ||
                      dictionary.storefront.home.hero_section.subheader,
                  }}
                />
              </div>

              {/* Registration button */}
              <>
                <div className="flex items-center justify-center lg:justify-between">
                  {!isAnyUserLoggedIn && (
                    <Link
                      href={storeFront.register.de()}
                      className="w-auto rounded-[100px] bg-primary px-8 py-3 text-center text-h5 font-bold leading-7 text-white transition-all hover:bg-primary-800 md:min-w-[245px] md:text-[26px]"
                      dangerouslySetInnerHTML={{
                        __html:
                          dictionary.storefront.home.hero_section.register_now,
                      }}
                    />
                  )}
                  <div className="hidden w-[160px] self-start lg:block">
                    <ImageWithFallback
                      src={
                        sectionData?.hero_img_right?.img_path ||
                        heroImage ||
                        '/hero-img.svg'
                      }
                      width={160}
                      height={160}
                      alt="Order Burger Area"
                      className="h-auto w-full object-contain"
                      priority
                    />
                  </div>
                </div>
              </>
            </div>
            {/* Right column */}
            <div className="flex justify-center lg:justify-end">
              {/* Online shop card */}
              <Link
                href={onlineShopLink}
                target={onlineShopLinkTarget}
                // onClick={() => {
                //   // Track event immediately without waiting for response
                //   trackAdcashButtonClick('hero_online_shop_link');
                // }}
                className="relative flex h-[220px] w-full items-end justify-start rounded-2xl sm:h-[300px] md:h-[400px] lg:h-[300px] xl:h-[400px]"
                style={{
                  backgroundImage: sectionData?.online_shop?.image_path
                    ? `url(${sectionData.online_shop?.image_path})`
                    : 'url(/hero-section/Titelbild_Website_November.png)',
                  backgroundPosition: 'center',
                  backgroundSize: isMobileAndTab ? 'cover' : '100%',
                  backgroundRepeat: 'no-repeat',
                }}
              >
                <button
                  type="button"
                  onClick={handleOnlineShopIconLink}
                  className="absolute -right-8 top-20 hidden w-[70px] lg:block"
                >
                  <ImageWithFallback
                    src={
                      sectionData?.online_shop?.right_image ?? `/cart-icon.png`
                    }
                    width={70}
                    height={70}
                    alt="Order Burger Area"
                    className="floating-with-delay h-auto w-full object-contain"
                    priority
                  />
                </button>
                <div className="pb-4 pl-6">
                  <button
                    type="button"
                    className="rounded-full bg-white bg-opacity-60 px-5 py-2 text-[22px] font-bold text-title backdrop-blur"
                  >
                    {
                      dictionary.storefront.home.hero_section
                        .online_shop_card_title
                    }{' '}
                    <span className="text-lg font-normal text-[#828282]">
                      ({totalOnlineShop})
                    </span>
                  </button>
                </div>
              </Link>
            </div>
          </div>
          {/* Bottom Row */}
          <div className="grid grid-cols-2 gap-y-5 lg:gap-x-8 lg:gap-y-0">
            {/* Contest banner image */}
            <div
              className={cn(
                'ht-promo-banner homepage-contest-banner relative col-span-full flex items-center justify-center rounded-2xl lg:col-span-1',
                sectionData?.ht_promo_banner?.bg_color
                  ? sectionData?.ht_promo_banner?.bg_color
                  : 'bg-[#132c3d]'
              )} /* GTM tracking attributes for banner analytics */
            >
              <Link
                href={sectionData?.ht_promo_banner?.link ?? contestBannerLink}
                className="block w-full"
                onClick={() => {
                  pushEvent({
                    event: 'banner_click',
                    eventOptions: {
                      banner_type: 'contest_banner',
                      banner_link:
                        sectionData?.ht_promo_banner?.link ?? contestBannerLink,
                      banner_location: 'homepage',
                    },
                  });
                }}
              >
                {/* REMOVE 'h-full' and 'absolute/fill'. 
                  Let the image dictate the height naturally using width/height props 
                  or standard responsive responsive sizing.*/}
                <Image
                  src={
                    isMobileAndTab
                      ? (sectionData?.ht_promo_banner?.mobile?.[lang] ??
                        defaultMobileHeroImage)
                      : (sectionData?.ht_promo_banner?.desktop?.[lang] ??
                        defaultDesktopHeroImage)
                  }
                  width={1200} // intrinsic width of your image
                  height={456} // intrinsic height of your image
                  style={{ width: '100%', height: 'auto' }} // Responsive standard
                  alt="Banner"
                  className="rounded-2xl"
                />
              </Link>
            </div>

            {/* App download & Social connect */}
            <div className="col-span-full flex flex-col items-center justify-start rounded-[20px] bg-white px-5 py-20 sm:px-10 lg:col-span-1 lg:flex-row lg:items-start lg:justify-between lg:py-10">
              {/* App download */}
              <div className="qr-app-section hidden items-start justify-start lg:flex lg:space-x-5">
                <div className="mt-2 rounded bg-white p-1">
                  <Image
                    src={sectionData?.qr_code?.img_path ?? '/qr-code.png'}
                    width={78}
                    height={78}
                    alt={sectionData?.qr_code?.label?.[lang] ?? 'QR Code'}
                  />
                </div>
                <div className="flex flex-col items-center space-y-3 text-center md:items-start md:text-left">
                  <h3 className="text-h5 font-[800] leading-[150%] text-title sm:text-h7 xl:text-h5">
                    {dictionary.storefront.home.hero_section.get_the_app}
                  </h3>
                  <div className="flex flex-col space-y-3">
                    <Link
                      href={sectionData?.play_store_link ?? '/'}
                      target="_blank"
                      className=""
                      rel="noopener noreferrer"
                    >
                      <Image
                        src="/play-store-rounded.png"
                        width={123}
                        height={41}
                        alt="Play Store"
                      />
                    </Link>
                    <Link
                      href={sectionData?.app_store_link ?? '/'}
                      target="_blank"
                      className=""
                      rel="noopener noreferrer"
                    >
                      <Image
                        src="/app-store-rounded.png"
                        width={123}
                        height={41}
                        alt="App Store"
                      />
                    </Link>
                  </div>
                </div>
              </div>
              {/* Social connect */}
              <div className="social-connect flex flex-col items-center space-y-3 text-center lg:items-start lg:text-left">
                <h3 className="text-h5 font-[800] leading-[150%] text-title sm:text-h7 lg:max-w-[160px] xl:text-h5">
                  {sectionData?.social_section?.title?.[lang] ?? ''}
                </h3>
                <div className="flex items-center justify-center">
                  {sectionData?.social_section?.facebook?.is_active ===
                    'yes' && (
                    <Link
                      href={
                        sectionData?.social_section?.facebook?.link ??
                        'https://www.facebook.com/groups/1478822456699039/'
                      }
                      target="_blank"
                      className=""
                      rel="noopener noreferrer"
                    >
                      <Image
                        src={
                          // sectionData?.social_section?.facebook?.image ??
                          '/icons/fb-circle.png'
                        }
                        width={58}
                        height={58}
                        alt="Facebook Group"
                        className="flex-1"
                      />
                    </Link>
                  )}
                  {sectionData?.social_section?.whatsapp?.is_active ===
                    'yes' && (
                    <Link
                      href={
                        sectionData?.social_section?.whatsapp?.link ??
                        'https://www.whatsapp.com/channel/0029Vb6KbrIFSAt2Lu3hxf2M'
                      }
                      target="_blank"
                      className=""
                      rel="noopener noreferrer"
                    >
                      <Image
                        src={
                          // sectionData?.social_section?.whatsapp?.image ??
                          '/icons/wa-circle.png'
                        }
                        width={58}
                        height={58}
                        className="flex-1"
                        alt="WhatsApp Community"
                      />
                    </Link>
                  )}

                  {sectionData?.social_section?.newsletter?.is_active ===
                    'yes' && (
                    <Link
                      href="#update"
                      className="flex max-w-[100px] items-center space-x-1"
                    >
                      <div className="">
                        {sectionData?.social_section?.newsletter?.[lang] ??
                          dictionary.storefront.home.hero_section
                            .subscribe_to_the_newsletter}
                      </div>
                      <Image
                        src={
                          // sectionData?.social_section?.whatsapp?.image ??
                          '/icons/email-subscribe.png'
                        }
                        width={58}
                        height={58}
                        className="flex-1"
                        alt="Email subscriptions"
                      />
                    </Link>
                  )}

                  {/* <Link
                      href="#update"
                      className="whitespace-nowrap text-h5 font-bold text-paragraph underline sm:text-h8 xl:text-h5"
                    >
                      {sectionData?.social_section?.newsletter?.[lang] ??
                        dictionary.storefront.home.hero_section
                          .subscribe_to_the_newsletter}
                    </Link> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
